import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Box,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  GetApp as ExportIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import FormConfigForm from "./FormConfigForm";
import SubmissionViewer from "./SubmissionViewer";
import {
  getAllFormConfigs,
  deleteFormConfig,
  toggleFormConfigStatus,
  exportFormTemplate,
} from "../../services/FormService";
import { URL_FORM } from "../../common/apiClient";

const FormManagement = () => {
  const [formConfigs, setFormConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showSubmissionDialog, setShowSubmissionDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);

  useEffect(() => {
    loadFormConfigs();
  }, []);

  const loadFormConfigs = async () => {
    try {
      setLoading(true);
      const response = await getAllFormConfigs();
      if (response.success) {
        setFormConfigs(response.data);
      } else {
        toast.error("Lỗi khi tải danh sách form");
      }
    } catch (error) {
      console.error("Error loading form configs:", error);
      toast.error("Lỗi khi tải danh sách form");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = () => {
    setSelectedForm(null);
    setShowFormDialog(true);
  };

  const handleEditForm = (form) => {
    setSelectedForm(form);
    setShowFormDialog(true);
  };

  const handleViewSubmissions = (form) => {
    setSelectedForm(form);
    setShowSubmissionDialog(true);
  };

  const handleDeleteForm = (form) => {
    setFormToDelete(form);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteFormConfig(formToDelete._id);
      if (response.success) {
        toast.success("Xóa form thành công");
        loadFormConfigs();
      } else {
        toast.error("Lỗi khi xóa form");
      }
    } catch (error) {
      console.error("Error deleting form:", error);
      toast.error("Lỗi khi xóa form");
    } finally {
      setDeleteDialogOpen(false);
      setFormToDelete(null);
    }
  };

  const handleToggleStatus = async (form) => {
    try {
      const response = await toggleFormConfigStatus(form._id);
      if (response.success) {
        toast.success(response.message);
        loadFormConfigs();
      } else {
        toast.error("Lỗi khi thay đổi trạng thái form");
      }
    } catch (error) {
      console.error("Error toggling form status:", error);
      toast.error("Lỗi khi thay đổi trạng thái form");
    }
  };

  const handleExportTemplate = async (form) => {
    try {
      const blob = await exportFormTemplate(form._id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `template_${form.name.replace(
        /[^a-zA-Z0-9]/g,
        "_"
      )}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Xuất template thành công");
    } catch (error) {
      console.error("Error exporting template:", error);
      toast.error("Lỗi khi xuất template");
    }
  };

  const handleCopyFormUrl = async (form) => {
    try {
      // Construct form URL - adjust this to match your frontend domain
      let baseUrl = window.location.origin;

      // If we're on admin subdomain, replace with main domain
      if (baseUrl.includes("admin.")) {
        baseUrl = baseUrl.replace("admin.", "");
      } else if (baseUrl.includes("/admin")) {
        baseUrl = baseUrl.replace("/admin", "");
      }

      const formUrl = `${URL_FORM}/form/${form._id}`;

      await navigator.clipboard.writeText(formUrl);
      toast.success(`Đã copy URL form vào clipboard: ${formUrl}`);
    } catch (error) {
      console.error("Error copying URL:", error);
      toast.error("Lỗi khi copy URL");
    }
  };

  const handleFormSaved = () => {
    setShowFormDialog(false);
    setSelectedForm(null);
    loadFormConfigs();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h4" component="h1">
            Quản lý Form
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateForm}
          >
            Tạo Form Mới
          </Button>
        </Box>

        {formConfigs.length === 0 ? (
          <Alert severity="info">
            Chưa có form nào được tạo. Hãy tạo form đầu tiên!
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tên Form</TableCell>
                  <TableCell>Mô tả</TableCell>
                  <TableCell>Số fields</TableCell>
                  <TableCell>Trạng thái</TableCell>
                  <TableCell>Ngày tạo</TableCell>
                  <TableCell>Thao tác</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formConfigs.map((form) => (
                  <TableRow key={form._id}>
                    <TableCell>
                      <Typography variant="subtitle2">{form.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {form.description || "Không có mô tả"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${form.fields?.length || 0} fields`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Switch
                          checked={form.isActive}
                          onChange={() => handleToggleStatus(form)}
                          size="small"
                        />
                        <Chip
                          label={
                            form.isActive ? "Hoạt động" : "Không hoạt động"
                          }
                          color={form.isActive ? "success" : "default"}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(form.createdAt).toLocaleDateString("vi-VN")}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleEditForm(form)}
                        color="primary"
                        size="small"
                        title="Chỉnh sửa"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleViewSubmissions(form)}
                        color="info"
                        size="small"
                        title="Xem submissions"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleCopyFormUrl(form)}
                        color="success"
                        size="small"
                        title="Copy URL form"
                      >
                        <CopyIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleExportTemplate(form)}
                        color="secondary"
                        size="small"
                        title="Xuất template"
                      >
                        <ExportIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteForm(form)}
                        color="error"
                        size="small"
                        title="Xóa"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog tạo/chỉnh sửa form */}
      <Dialog
        open={showFormDialog}
        onClose={() => setShowFormDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedForm ? "Chỉnh sửa Form" : "Tạo Form Mới"}
        </DialogTitle>
        <DialogContent>
          <FormConfigForm
            formConfig={selectedForm}
            onSave={handleFormSaved}
            onCancel={() => setShowFormDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog xem submissions */}
      <Dialog
        open={showSubmissionDialog}
        onClose={() => setShowSubmissionDialog(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>Submissions - {selectedForm?.name}</DialogTitle>
        <DialogContent>
          <SubmissionViewer
            formConfig={selectedForm}
            onClose={() => setShowSubmissionDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog xác nhận xóa */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn xóa form "{formToDelete?.name}"? Hành động này
            không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Hủy</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FormManagement;
